import type {
  AgentInstallView,
  DrainOutcome,
  EnqueueResult,
  EnrollmentIssued,
  NodeDetailView,
  NodeRemnantView,
  NodeView,
  RegisterHostRequest,
  RegisterNodeRequest,
  ServerInventoryView,
  SshHostKeyView,
  SshInstallRequest,
} from '@/types/api';
import { apiRequest } from '@/api/client';

/** Servers: the 2d2s inventory, the nodes standing on it, and what is left on them. */
export const nodesApi = {
  list: () => apiRequest<NodeView[]>('/nodes'),

  /** A server's own page (ADR-0058). */
  get: (id: string) => apiRequest<NodeDetailView>(`/nodes/${id}`),

  servers: () =>
    apiRequest<{ servers: ServerInventoryView[]; scopeTag: string | null; syncAvailable: boolean }>('/servers'),

  syncServers: (key: string) => apiRequest<EnqueueResult>('/servers/sync', { method: 'POST', idempotencyKey: key }),

  register: (input: RegisterNodeRequest) => apiRequest<EnrollmentIssued>('/nodes', { method: 'POST', body: input }),

  /** An external server whose own sites the panel backs up (ADR-0033). */
  registerHost: (input: RegisterHostRequest) => apiRequest<EnrollmentIssued>('/nodes/hosts', { method: 'POST', body: input }),

  /** Looks at what an external server serves, now. */
  discover: (id: string, key: string) => apiRequest<EnqueueResult>(`/nodes/${id}/discover`, { method: 'POST', idempotencyKey: key }),

  reissueEnrollmentToken: (id: string) =>
    apiRequest<EnrollmentIssued>(`/nodes/${id}/enrollment-token`, { method: 'POST' }),

  /** The server's SSH host key, to confirm before a login goes to it (ADR-0036). */
  sshHostKey: (id: string, port: number) => apiRequest<SshHostKeyView>(`/nodes/${id}/ssh/host-key`, { method: 'POST', body: { port } }),

  /** Logs in once and installs the agent; the login is kept nowhere. */
  installAgent: (id: string, input: SshInstallRequest) => apiRequest<AgentInstallView>(`/nodes/${id}/ssh/install`, { method: 'POST', body: input }),

  agentInstall: (installId: string) => apiRequest<AgentInstallView>(`/nodes/agent-installs/${installId}`),

  setScheduling: (id: string, state: 'active' | 'cordoned') =>
    apiRequest<{ schedulingState: string }>(`/nodes/${id}/scheduling`, { method: 'POST', body: { state } }),

  remnants: (id: string) => apiRequest<NodeRemnantView[]>(`/nodes/${id}/remnants`),

  revokeAgent: (id: string, confirmation: string) =>
    apiRequest<{ revoked: boolean }>(`/nodes/${id}/revoke-agent`, { method: 'POST', body: { confirmation } }),

  drain: (id: string, confirmation: string, key: string) =>
    apiRequest<DrainOutcome>(`/nodes/${id}/drain`, { method: 'POST', body: { confirmation }, idempotencyKey: key }),

  /** Removes a stopped copy a site left on a server it moved away from. */
  reclaimRemnant: (id: string, key: string) =>
    apiRequest<EnqueueResult>(`/remnants/${id}/reclaim`, { method: 'POST', idempotencyKey: key }),
};
